import {
    ApolloClient,
    ApolloLink,
    HttpLink,
    InMemoryCache,
    split,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { createGraphQLWSLink } from "./graphqlWSAdapter";

const API_URL = import.meta.env.VITE_VANDERHEIM_API_URL as string;
const WS_API_URL = import.meta.env.VITE_VANDERHEIM_WS_API_URL as string;

const httpLink = new HttpLink({
    uri: API_URL,
    credentials: "include",
});

const csrfLink = new ApolloLink((operation, forward) => {
    const csrfToken = document.cookie
        .split("; ")
        .find((row) => row.startsWith("csrftoken="));
    if (csrfToken) {
        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                "X-CSRFToken": csrfToken.split("=")[1],
            },
        }));
    }
    return forward(operation);
});

const wsLink = createGraphQLWSLink({
    url: WS_API_URL,
    connectionParams: {
        credentials: "include",
    },
    keepAlive: 30000, // Send ping every 30 seconds to keep connection alive
    retryAttempts: 5,
    shouldRetry: () => true,
    retryWait: async (retries: number) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        const waitTime = Math.min(1000 * Math.pow(2, retries), 16000);
        console.debug(
            `[WebSocket] Retrying connection in ${waitTime}ms (attempt ${retries + 1})`,
        );
        await new Promise((resolve) => setTimeout(resolve, waitTime));
    },
    on: {
        connected: () => console.debug("[WebSocket] Connected successfully"),
        closed: (event: unknown) => {
            const closeEvent = event as CloseEvent;
            console.debug(
                `[WebSocket] Connection closed: code=${closeEvent.code}, reason=${closeEvent.reason}`,
            );
        },
        error: (error: unknown) => {
            console.error("[WebSocket] Connection error:", error);
        },
    },
});

const splitLink = split(
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
        );
    },
    wsLink,
    ApolloLink.from([csrfLink, httpLink]),
);

const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                topPlayersByClanGrouped: {
                    keyArgs: ["seasonId"],
                    merge(existing = [], incoming = [], { args }) {
                        const offsets = args?.clanOffsets || {};
                        const isInitialLoad = Object.keys(offsets).length === 0;

                        console.debug(
                            "[Apollo Merge] Incoming offsets:",
                            offsets,
                        );
                        console.debug("[Apollo Merge] Existing:", existing);
                        console.debug("[Apollo Merge] Incoming:", incoming);

                        const merged: Record<string, any[]> = {};

                        for (const group of existing) {
                            merged[group.clanTag] = group.players ?? [];
                        }

                        for (const group of incoming) {
                            const tag = group.clanTag;
                            const offset = offsets?.[tag] ?? 0;

                            if (!isInitialLoad && !(tag in offsets)) {
                                continue;
                            }

                            const existingPlayers = merged[tag] ?? [];
                            const existingIds = new Set(
                                existingPlayers.map((p: any) => p.playerId),
                            );
                            const newPlayers = group.players.filter(
                                (p: any) => !existingIds.has(p.playerId),
                            );

                            merged[tag] = [
                                ...existingPlayers.slice(0, offset),
                                ...newPlayers,
                            ];
                        }

                        const final = Object.entries(merged).map(
                            ([clanTag, players]) => ({
                                clanTag,
                                players,
                            }),
                        );

                        console.debug("[Apollo Merge] Merged result:", final);
                        return final;
                    },
                },

                topPlayersCurrentSeason: {
                    keyArgs: false,
                    merge(existing = [], incoming = [], { args }) {
                        const offset = args?.offset ?? 0;
                        return [
                            ...(existing ?? []).slice(0, offset),
                            ...incoming,
                        ];
                    },
                },

                topPlayersPreviousSeason: {
                    keyArgs: ["seasonId"],
                    merge(existing = [], incoming = [], { args }) {
                        const offset = args?.offset ?? 0;
                        return [
                            ...(existing ?? []).slice(0, offset),
                            ...incoming,
                        ];
                    },
                },

                topPlayersLifetime: {
                    keyArgs: false,
                    merge(existing = [], incoming = [], { args }) {
                        const offset = args?.offset ?? 0;
                        return [
                            ...(existing ?? []).slice(0, offset),
                            ...incoming,
                        ];
                    },
                },

                topPlayersShieldValue: {
                    keyArgs: false,
                    merge(existing = [], incoming = [], { args }) {
                        const offset = args?.offset ?? 0;
                        return [
                            ...(existing ?? []).slice(0, offset),
                            ...incoming,
                        ];
                    },
                },
            },
        },
    },
});

const client = new ApolloClient({
    link: splitLink,
    cache,
});

export default client;
